<template>
  <div
    class="c-registration__payment-data"
    :class="{
      'c-registration-step--inactive': isDisbaled,
      'c-registration-step--submitted': isSubmitted,
    }"
  >
    <div v-if="hasAppUpdateError" class="c-registration__error">
      {{ $t('dbc-error-app-update') }}
    </div>

    <div
      class="c-registration-step__payment"
      v-if="!isSubmitted && !isSepaVerify"
    >
      <div v-if="hasFinishSepaVerificationError" class="c-registration__error">
        {{ $t('dbc-error-verify-failed') }}
      </div>

      <RegistrationPaymentMethodGroup
        :paymentMethods="paymentMethods"
        @changePaymentMethod="changePaymentMethod"
      />

      <div v-if="stage !== 'prod' && paymentMethod === 'sepa' && !isSubmitted">
        <RegistrationPaymentSepaTEST
          :sepaVerifyTimestamp="sepaVerifyTimestamp"
          @submitPaymentData="handleSepaVerifyTest"
          @isEditable="emit('isEditable', true)"
          @isSubmitted="isSubmitted = true"
        />
      </div>

      <div
        v-bind:style="{ height: paymentWidgetHeight }"
        class="c-payment-widget"
        id="paymentWidgetContainer"
      >
        <div v-if="hasPaymentError" class="c-registration__error">
          {{ $t('dbc-payment-error1') }}
          <NuxtLink
            class="c-registration__link"
            v-on:click.prevent="tryAgainAfterError"
            >{{ $t('dbc-text-here') }}
          </NuxtLink>
          {{ $t('dbc-payment-error2') }}
        </div>

        <div v-if="hasDachError" class="c-registration__error">
          {{ $t('dbc-dach-error') }}
        </div>
      </div>
    </div>

    <div
      class="c-registration-form__summary"
      v-if="isSubmitted || isSepaVerify"
    >
      <RegistrationPaymentSummary :paymentResult="paymentResult" />

      <RegistrationSuccessTick v-if="isSubmitted"></RegistrationSuccessTick>
    </div>

    <div v-if="hasStartSepaVerificationError" class="c-registration__error">
      {{ $t('dbc-error-something-went-wrong') }}
    </div>

    <button
      v-if="!isSubmitted && !isSepaVerify"
      class="dbc-gc-btn dbc-gc-btn--primary c-registration-button c-registration-button--primary"
      :class="{
        'c-registration-button--loading': isLoading,
      }"
      :disabled="isLoading"
      v-on:click.prevent="submitPayment"
    >
      {{ $t('dbc-btn-check') }}
    </button>

    <button
      v-if="isSepaVerify"
      v-on:click="verifySepa"
      class="dbc-gc-btn dbc-gc-btn--primary c-registration-button"
    >
      {{ $t('dbc-btn-verify') }}
    </button>

    <DbcBanner
      class="w-2/5 mt-s max-tablet:w-full"
      v-if="isSubmitted && !isDACH"
      :severity="'info'"
      :message="$t('dbc-text-manual-verification')"
    />
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  paymentMethods: string[]
  paymentSectionRef: HTMLElement | null
}>()

const emit = defineEmits<{
  (e: 'submitPaymentData', paymentData: PaymentData | null): void
  (e: 'isEditable', isEditable: boolean): void
  (e: 'onNexiError'): void
}>()

const { locale } = useI18n()
const paymentMethod = useState<'sepa' | 'creditcard'>('dbc-payment-method')
const isDACH = useState('isDACH')
const mobileOs = useMobileOs()

const hasAppUpdateError = ref(false)
const hasDachError = ref(false)
const hasFinishSepaVerificationError = ref(false)
const hasPaymentError = ref(false)
const hasStartSepaVerificationError = ref(false)
const isDisbaled = ref(false)
const isLoading = ref(false)
const isSepaVerify = ref(false)
const isSubmitted = ref(false)
const paymentInstrumentId = ref('')
const paymentResult = ref<PaymentData | null>(null)
const paymentWidgetRef = ref(null)
const sepaVerifyTimestamp = ref('')
const tinkRedirect = ref('')
const paymentWidgetIframeHeight = ref<PaymentWidgetIframeHeight | null>(null)
const paymentWidgetHeight = ref<string>('')

onMounted(() => {
  const channel = new BroadcastChannel('sepa-verify-channel')
  channel.addEventListener('message', (event) => {
    onVerifySepaComplete(event.data)
  })

  window.addEventListener('sepa-verify-complete', (event: any) => {
    const sepaVerifyData = JSON.parse(window.atob(event.detail.sepaVerifyData))
    onVerifySepaComplete(sepaVerifyData)
  })

  configureConcardisWidget()
  window.PayEngine.setPublishableKey(
    useRuntimeConfig().public.nexi.publishableKey
  )

  paymentWidgetIframeHeight.value = {
    creditcard: {
      default: '400px',
      error: mobileOs.value === '' ? '460px' : '520px',
    },
    sepa: {
      default: mobileOs.value === '' ? '28em' : '34em',
      error: mobileOs.value === '' ? '32em' : '39em',
    },
  }

  paymentWidgetHeight.value =
    paymentWidgetIframeHeight.value.creditcard!.default
})

const runtimeConfig = useRuntimeConfig()

function configureConcardisWidget() {
  const optionalParameters = {
    language: locale.value,
    products: [paymentMethod.value],
    customStyleId: runtimeConfig.public.nexiCustomStyleId,
    hidePayButton: true,
    hideTitleIcons: true,
    skipFinalResultView: true,
    initCallback: initPaymentWidgetCallback,
    optionalFields: {
      creditCard: {
        cardHolder: true,
        cvcNeeded: true,
        cofContract: {
          type: 'UNSCHEDULED',
        },
      },
      sepa: {
        bic: true,
        bankName: false,
        requestMandate: true,
        directDebitType: 'FIRST',
      },
    },
  }

  try {
    window.PayEngineWidget.initAsInlineComponentPi(
      'paymentWidgetContainer',
      useRuntimeConfig().public.nexi.publishableKey,
      optionalParameters,
      piCreateCallback
    )
  } catch (error) {
    hasAppUpdateError.value = true
    console.error(`Could not load payment widget: ${error}`)
  }
}

function piCreateCallback(error: any, paymentInstrument: any) {
  isLoading.value = true

  if (error) {
    if (error.error === 'Iframe timeout error') {
      emit('onNexiError')
    } else {
      hasPaymentError.value = true
      removeAllIframes()
    }
    return
  }

  paymentInstrumentId.value = paymentInstrument.paymentInstrumentId
  paymentResult.value = paymentInstrument

  if (paymentInstrument.type === 'creditcard') {
    isDACH.value = true
    window.PayEngine.threeDsAuthentication(
      paymentInstrumentId.value,
      'paymentWidgetContainer',
      {
        type: 'RECURRING_CARDCHECK',
        transactionData: { amount: 0, currency: 'EUR' },
        async: {
          successUrl: 'https://www.callabike.de',
          failureUrl: 'https://www.callabike.de',
        },
      },
      threeDsAuthenticationCallback
    )
  }

  if (paymentInstrument.type === 'sepa') {
    sepaVerifyTimestamp.value = Date.now().toString()
    const iban = paymentInstrument.attributes.iban
    isDACH.value = new RegExp('^[DE | AT | CH ]{2}').test(iban)

    if (!isDACH.value) {
      onVerifySepaComplete({
        result: 'success',
        timestamp: sepaVerifyTimestamp.value,
      })
      return
    }

    fetchSepaVerify(
      locale.value,
      iban,
      paymentResult.value?.attributes.accountHolder,
      sepaVerifyTimestamp.value
    ).then((response) => {
      isLoading.value = false
      if (response.ok) {
        isSepaVerify.value = true
        hasStartSepaVerificationError.value = false
        tinkRedirect.value = response._data.data.redirecturl
        if (paymentResult.value) {
          paymentResult.value.zahlungsReferenzId = response._data.data._id
        }
        trackEvent('Tink-Flow', 'Fetch Redirect URL erfolgreich')
        emit('isEditable', true)
        props.paymentSectionRef?.scrollIntoView(true)
      }
      if (!response.ok) {
        hasStartSepaVerificationError.value = true
        trackEvent('Tink-Flow', 'Fetch Redirect URL fehlerhaft')
      }
    })
  }
}

function threeDsAuthenticationCallback(error: any, result: any) {
  isLoading.value = false
  if (error) {
    removeAllIframes()
    hasPaymentError.value = true
    return
  }

  if (result.threeDsVersion === null) {
    removeAllIframes()
    hasPaymentError.value = true
    return
  }

  if (result.threeDsVersion === '1.0') {
    removeAllIframes()
    hasPaymentError.value = true
    return
  }

  if (result.threeDsVersion === '2.0') {
    window.PayEngine.cardCheck(
      paymentInstrumentId.value,
      'UNSCHEDULED',
      null,
      result.specificData.authenticationId,
      onCardCheckCallback
    )
  }
}

function onCardCheckCallback(error: any, result: any) {
  if (error) {
    removeAllIframes()
    hasPaymentError.value = true
    return
  }
  if (result.status === 'FAILURE') {
    removeAllIframes()
    hasPaymentError.value = true
    return
  }

  if (paymentResult.value) {
    paymentResult.value.threeDsAuthenticationId =
      result.threeDsData.threeDsAuthenticationId

    isSubmitted.value = true
    emit('submitPaymentData', paymentResult.value)
  }
  props.paymentSectionRef?.scrollIntoView(true)
}

function initPaymentWidgetCallback(error: any, result: any) {
  if (error) {
    hasPaymentError.value = true
    return
  }
  paymentWidgetRef.value = result
}

function changePaymentMethod() {
  hasDachError.value = false
  hasPaymentError.value = false
  hasAppUpdateError.value = false
  removeAllIframes()
  configureConcardisWidget()
  paymentWidgetHeight.value = paymentWidgetIframeHeight.value
    ? paymentWidgetIframeHeight.value[paymentMethod.value]!.default
    : ''
}

function changePaymentData() {
  isSubmitted.value = false
  isSepaVerify.value = false
  isDisbaled.value = false
  paymentResult.value = null
  paymentWidgetRef.value = null
  sepaVerifyTimestamp.value = ''

  nextTick(() => {
    removeAllIframes()
    configureConcardisWidget()
  })
}

function tryAgainAfterError() {
  hasPaymentError.value = false
  configureConcardisWidget()
}

function submitPayment() {
  hasFinishSepaVerificationError.value = false

  window.PayEngineWidget.validate(
    paymentWidgetRef.value,
    (error: any, result: any) => {
      if (error) {
        paymentWidgetHeight.value = paymentWidgetIframeHeight.value
          ? paymentWidgetIframeHeight.value[paymentMethod.value]!.error
          : ''
      }
      if (result) {
        window.PayEngineWidget.pay(paymentWidgetRef.value)
        paymentWidgetHeight.value = paymentWidgetIframeHeight.value
          ? paymentWidgetIframeHeight.value[paymentMethod.value]!.default
          : ''
      }
    }
  )
}

function removeAllIframes() {
  const container = document.querySelector('.c-payment-widget')
  container?.querySelectorAll('iframe').forEach((iframe) => iframe.remove())
  isLoading.value = false
}

function verifySepa() {
  trackEvent('Tink-Flow', 'Start erfolgreich')
  isDisbaled.value = true
  const tinkWindow = window.open(tinkRedirect.value)

  if (
    !tinkWindow ||
    tinkWindow.closed ||
    typeof tinkWindow.closed == 'undefined'
  ) {
    trackEvent('Tink-Flow', 'Start fehlerhaft')
    hasAppUpdateError.value = true
    changePaymentData()
  }
}

function onVerifySepaComplete(data: any) {
  isDisbaled.value = false
  if (
    data.result === 'success' &&
    data.timestamp === sepaVerifyTimestamp.value
  ) {
    if (isDACH.value) {
      trackEvent('Tink-Flow', 'Ende erfolgreich')
    } else {
      trackEvent('SEPA nicht-DACH', 'nicht-DACH IBAN eingegeben')
    }
    isSepaVerify.value = false
    isSubmitted.value = true
    emit('submitPaymentData', paymentResult.value)
  } else if (
    data.result === 'error' ||
    data.timestamp !== sepaVerifyTimestamp.value
  ) {
    trackEvent('Tink-Flow', 'Ende fehlerhaft')
    isSepaVerify.value = false
    hasFinishSepaVerificationError.value = true
    emit('isEditable', false)
    changePaymentData()
  }
}

// *TEST* SEPA Verify
const config = useRuntimeConfig()
const stage = config.public.stage

function handleSepaVerifyTest(result: PaymentData | null) {
  paymentResult.value = result
  emit('submitPaymentData', paymentResult.value)
}

defineExpose({
  changePaymentData,
})
</script>
